import { useState, useRef, useEffect, useId } from 'react';
import { Search, X, UserPlus, Loader2 } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import { auth, functions } from '../../config/firebase';
import { useDebounce } from '../../hooks/useDebounce';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useContact } from '../../hooks/useContacts';
import { useContactPickerSearch } from '../../hooks/useContactPicker';
import { SYSTEM_CREATOR_ID } from '../../config/constants';
import type { Contact } from '../../types/lead';
import type { ContactPickerResult } from '../../hooks/useContactPicker';
import { logCallableFailure } from '../../lib/logCallableFailure';

export interface ContactPickerProps {
  onSelect: (contact: Contact) => void;
  onClear?: () => void;
  selectedContactId?: string;
  placeholder?: string;
  disabled?: boolean;
}

interface QuickCreateForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  title: string;
}

const EMPTY_FORM: QuickCreateForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  companyName: '',
  title: '',
};

/**
 * Shared typeahead contact picker with AppSheet lazy migration and quick-create.
 *
 * States:
 * 1. Selected contact: shows name + clear button
 * 2. No selection: shows search input with dropdown
 * 3. Quick-create form (expandable from dropdown footer)
 */
export function ContactPicker({
  onSelect,
  onClear,
  selectedContactId,
  placeholder = 'Search contacts...',
  disabled = false,
}: ContactPickerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [quickCreateForm, setQuickCreateForm] = useState<QuickCreateForm>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  const { currentOrganization } = useOrganization();
  const debouncedSearch = useDebounce(searchTerm, 300);

  // Fetch selected contact for display
  const { data: selectedContact, isLoading: isLoadingSelected } = useContact(selectedContactId);

  // Search results
  const { data: searchResults = [], isLoading: isSearching } = useContactPickerSearch(
    debouncedSearch
  );

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowQuickCreate(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Open dropdown when typing starts
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value.length > 0) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    if (searchTerm.length > 0 || showQuickCreate) {
      setIsOpen(true);
    }
  };

  const handleSelectResult = (result: ContactPickerResult) => {
    setIsOpen(false);
    setSearchTerm('');
    setShowQuickCreate(false);
    onSelect(result.contact);
  };

  const handleClear = () => {
    setSearchTerm('');
    setIsOpen(false);
    setShowQuickCreate(false);
    onClear?.();
  };

  const handleQuickCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);

    if (!quickCreateForm.firstName.trim()) {
      setSaveError('First name is required');
      return;
    }
    if (!currentOrganization?.organizationId) {
      setSaveError('No organization selected');
      return;
    }

    setIsSaving(true);
    try {
      const now = Timestamp.now();
      const firstName = quickCreateForm.firstName.trim();
      const lastName = quickCreateForm.lastName.trim();
      const fullName = `${firstName} ${lastName}`.trim();
      const normalizedPhone = quickCreateForm.phone
        ? quickCreateForm.phone.replace(/\D/g, '').replace(/^1(\d{10})$/, '$1')
        : undefined;
      const normalizedEmail = quickCreateForm.email
        ? quickCreateForm.email.trim().toLowerCase()
        : undefined;

      const newContactData: Omit<Contact, 'contactId'> = {
        organizationId: currentOrganization.organizationId,
        firstName,
        lastName,
        fullName,
        email: normalizedEmail || '',
        phone: quickCreateForm.phone.trim() || '',
        companyName: quickCreateForm.companyName.trim() || '',
        title: quickCreateForm.title.trim() || '',
        source: 'manual',
        normalizedPhone,
        normalizedEmail,
        createdAt: now,
        updatedAt: now,
        createdBy: auth.currentUser?.uid ?? SYSTEM_CREATOR_ID,
      };

      const createFn = httpsCallable<unknown, { contactId: string }>(functions, 'shared_createContact');
      const createResult = await createFn({ organizationId: currentOrganization.organizationId, contactData: newContactData });

      const newContact: Contact = { ...newContactData, contactId: createResult.data.contactId };
      onSelect(newContact);

      // Reset
      setQuickCreateForm(EMPTY_FORM);
      setShowQuickCreate(false);
      setIsOpen(false);
      setSearchTerm('');
    } catch (err) {
      logCallableFailure('Failed to create contact:', err);
      setSaveError('Failed to create contact. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const showDropdown = isOpen && !disabled;
  const hasResults = searchResults.length > 0;
  const showEmptyState = debouncedSearch.length >= 2 && !isSearching && !hasResults;

  // ── SELECTED STATE ──────────────────────────────────────────────────────────
  if (selectedContactId) {
    return (
      <div className="flex items-center gap-2 min-h-[44px] px-3 py-2 rounded-xl border border-slate-300 shadow-sm bg-white">
        {isLoadingSelected ? (
          <Loader2 className="w-4 h-4 text-slate-400 animate-spin flex-shrink-0" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-semibold text-sky-700">
              {selectedContact
                ? `${selectedContact.firstName[0] ?? ''}${selectedContact.lastName[0] ?? ''}`.toUpperCase()
                : '?'}
            </span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-900 truncate text-sm">
            {isLoadingSelected
              ? 'Loading…'
              : selectedContact?.fullName ?? 'Unknown Contact'}
          </div>
          {selectedContact?.companyName && (
            <div className="text-xs text-slate-500 truncate">{selectedContact.companyName}</div>
          )}
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0 -mr-2"
            aria-label="Clear contact"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  // ── SEARCH + DROPDOWN STATE ─────────────────────────────────────────────────
  return (
    <div className="relative" ref={containerRef}>
      {/* Search input */}
      <div
        className={`flex items-center gap-2 min-h-[44px] px-3 py-2 rounded-lg border transition-colors ${
          disabled
            ? 'border-slate-200 bg-slate-50 cursor-not-allowed'
            : isOpen
            ? 'border-sky-400 ring-2 ring-sky-100 bg-white'
            : 'border-slate-200 bg-white hover:border-slate-300'
        }`}
      >
        <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 bg-transparent text-sm text-slate-900 placeholder:text-slate-400 outline-none disabled:cursor-not-allowed"
          aria-label="Search contacts"
          aria-expanded={showDropdown}
          aria-controls={showDropdown && !isSearching && hasResults ? listboxId : undefined}
          role="combobox"
          aria-autocomplete="list"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => {
              setSearchTerm('');
              setIsOpen(false);
              inputRef.current?.focus();
            }}
            className="text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
            aria-label="Clear search"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 z-50 max-h-80 overflow-y-auto">
          {/* Search results */}
          {isSearching && (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Searching…
            </div>
          )}

          {!isSearching && hasResults && (
            <ul id={listboxId} role="listbox" aria-label="Contact results">
              {searchResults.map((result: ContactPickerResult) => (
                <li key={`${result.origin}-${result.contact.contactId}`} role="option" aria-selected={false}>
                  <button
                    type="button"
                    onClick={() => handleSelectResult(result)}
                    className="w-full flex items-center gap-3 px-4 min-h-[44px] py-2 text-left hover:bg-slate-50 transition-colors"
                  >
                    {/* Avatar */}
                    <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-semibold text-slate-600">
                        {`${result.contact.firstName[0] ?? ''}${result.contact.lastName[0] ?? ''}`.toUpperCase() || '?'}
                      </span>
                    </div>

                    {/* Name + company */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-medium text-sm text-slate-900 truncate">
                          {result.contact.fullName}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 truncate">
                        {result.contact.companyName && (
                          <span className="truncate">{result.contact.companyName}</span>
                        )}
                        {result.contact.email && (
                          <span className="truncate">{result.contact.email}</span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {showEmptyState && (
            <div className="px-4 py-3 text-sm text-slate-500">
              No contacts found for "{debouncedSearch}"
            </div>
          )}

          {debouncedSearch.length < 2 && !isSearching && (
            <div className="px-4 py-3 text-sm text-slate-400">
              Type at least 2 characters to search
            </div>
          )}

          {/* Divider before actions */}
          <div className="border-t border-slate-100" />

          {/* Quick-create toggle */}
          {!showQuickCreate && (
            <button
              type="button"
              onClick={() => setShowQuickCreate(true)}
              className="w-full flex items-center gap-2 px-4 min-h-[44px] py-2 text-sm text-sky-600 hover:bg-sky-50 transition-colors font-medium"
            >
              <UserPlus className="w-4 h-4 flex-shrink-0" />
              Create new contact
            </button>
          )}

          {/* Quick-create form */}
          {showQuickCreate && (
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                  <UserPlus className="w-4 h-4" />
                  New Contact
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setShowQuickCreate(false);
                    setQuickCreateForm(EMPTY_FORM);
                    setSaveError(null);
                  }}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                  aria-label="Cancel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <form onSubmit={handleQuickCreateSubmit} className="space-y-2">
                {/* Name row */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={quickCreateForm.firstName}
                      onChange={(e) =>
                        setQuickCreateForm((f) => ({ ...f, firstName: e.target.value }))
                      }
                      placeholder="Jane"
                      className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-100"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={quickCreateForm.lastName}
                      onChange={(e) =>
                        setQuickCreateForm((f) => ({ ...f, lastName: e.target.value }))
                      }
                      placeholder="Smith"
                      className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-100"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
                  <input
                    type="email"
                    value={quickCreateForm.email}
                    onChange={(e) =>
                      setQuickCreateForm((f) => ({ ...f, email: e.target.value }))
                    }
                    placeholder="jane@example.com"
                    className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-100"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={quickCreateForm.phone}
                    onChange={(e) =>
                      setQuickCreateForm((f) => ({ ...f, phone: e.target.value }))
                    }
                    placeholder="(555) 123-4567"
                    className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-100"
                  />
                </div>

                {/* Company */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Company</label>
                  <input
                    type="text"
                    value={quickCreateForm.companyName}
                    onChange={(e) =>
                      setQuickCreateForm((f) => ({ ...f, companyName: e.target.value }))
                    }
                    placeholder="Acme Corp"
                    className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-100"
                  />
                </div>

                {/* Title */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Title</label>
                  <input
                    type="text"
                    value={quickCreateForm.title}
                    onChange={(e) =>
                      setQuickCreateForm((f) => ({ ...f, title: e.target.value }))
                    }
                    placeholder="Operations Manager"
                    className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-100"
                  />
                </div>

                {saveError && (
                  <p className="text-xs text-red-600">{saveError}</p>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="flex-1 flex items-center justify-center gap-2 min-h-[44px] px-3 py-2 bg-sky-600 hover:bg-sky-700 disabled:bg-sky-400 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4" />
                        Create Contact
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowQuickCreate(false);
                      setQuickCreateForm(EMPTY_FORM);
                      setSaveError(null);
                    }}
                    disabled={isSaving}
                    className="min-h-[44px] px-3 py-2 text-sm text-slate-600 hover:text-slate-800 border border-slate-300 rounded-xl shadow-sm transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
